import { HideImage, QuestionMark } from '@mui/icons-material'
import { Button, Dialog, DialogContent, Grid } from '@mui/material'
import { ExplanationAccordion } from 'components'
import * as controllers from 'controllers'
import { vehicleCheckInPassengerAirbagPhotoValidationSchema, VehicleCheckInPassengerAirbagPhotoDto } from 'dtos'
import { Formik } from 'formik'
import {
  CheckInPhotoBrowseTakeRow,
  CheckInPhotoDialogNavProps,
  CheckInPhotoDialogTitleRow,
  CheckInPhotoPreview,
  CheckInPhotoStackedNavButtons,
  useCheckInPhotoSubmitIntent,
  useLifecycleDocumentPreview
} from './checkInPhotoDialogCommon'

interface PassengerAirbagPhotoDialogProps {
  initialValues: VehicleCheckInPassengerAirbagPhotoDto
  isLoading: boolean
  nav?: CheckInPhotoDialogNavProps
  onClose: () => void
  onDelete: () => void
  onSave: (values: VehicleCheckInPassengerAirbagPhotoDto, options?: { goToNext?: boolean }) => void
  open: boolean
  vehicleGuid: string
}

export default function PassengerAirbagPhotoDialog({
  initialValues,
  isLoading,
  nav,
  onClose,
  onDelete,
  onSave,
  open,
  vehicleGuid
}: PassengerAirbagPhotoDialogProps) {
  const { onFormSubmit, submitClose, submitThenNext } = useCheckInPhotoSubmitIntent(onSave)
  const serverSrc = useLifecycleDocumentPreview(
    vehicleGuid,
    initialValues.documentGuid,
    initialValues.displayFileName,
    controllers.getVehicleCheckInPhotoBase64
  )

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validateOnChange={true}
        validateOnBlur={false}
        validationSchema={vehicleCheckInPassengerAirbagPhotoValidationSchema}
        onSubmit={onFormSubmit}
      >
        {({ isSubmitting, setFieldValue, submitForm, values }) => {
          return (
            <form onSubmit={e => void e.preventDefault()}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <CheckInPhotoDialogTitleRow onClose={onClose} title='Passenger Airbag Photo Details' />

                  <Grid item xs={12}>
                    <CheckInPhotoPreview
                      alt='Passenger Airbag'
                      file={values.vehicleCheckInPassengerAirbagPhotoFile}
                      placeholderSrc='/11 Passenger Airbag.jpg'
                      serverSrc={serverSrc}
                    />
                  </Grid>

                  <CheckInPhotoBrowseTakeRow
                    browseInputId='passenger-airbag-browse'
                    cameraInputId='passenger-airbag-camera'
                    disabled={isSubmitting || isLoading || values.vehicleCheckInHasPassengerAirbag === false}
                    onFileChosen={file => setFieldValue('vehicleCheckInPassengerAirbagPhotoFile', file)}
                  />

                  <Grid item xs={12}>
                    <Button
                      color='error'
                      disabled={
                        isSubmitting ||
                        isLoading ||
                        !values.vehicleCheckInHasPassengerAirbag ||
                        !Boolean(values.documentGuid)
                      }
                      endIcon={<HideImage />}
                      fullWidth
                      onClick={() => {
                        if (window.confirm('Are you sure you would like to delete the uploaded Passenger Airbag Photo?')) {
                          onDelete()
                        }
                      }}
                      variant='outlined'
                    >
                      DELETE UPLOADED PHOTO
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      color='primary'
                      disabled={isSubmitting || isLoading}
                      endIcon={<QuestionMark />}
                      fullWidth
                      onClick={() => {
                        setFieldValue('vehicleCheckInHasPassengerAirbag', !(values.vehicleCheckInHasPassengerAirbag ?? true))
                      }}
                      variant={values.vehicleCheckInHasPassengerAirbag === false ? 'contained' : 'outlined'}
                    >
                      VEHICLE DOES NOT HAVE PASSENGER AIRBAG
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <ExplanationAccordion variant='outlined'>
                      || Pressing “Previous” goes to the previous photo without saving. ||
                    </ExplanationAccordion>
                  </Grid>

                  <CheckInPhotoStackedNavButtons
                    isLoading={isLoading}
                    isSubmitting={isSubmitting}
                    nav={nav}
                    submitClose={submitClose}
                    submitForm={submitForm}
                    submitThenNext={submitThenNext}
                  />
                </Grid>
              </DialogContent>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
